import type { GroupPublic } from '@repo/types/groups';
import type { QuizPublic } from '@repo/types/quiz';
import type { UserPublic } from '@repo/types/users';

import { cn } from '@repo/lib/cn';
import { AlertCircleIcon, Loader2Icon, MinusIcon, PlusIcon, UserIcon, UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import AssignmentBuilderDateTimeSelector from '@/components/features/assignments/builder/AssignmentBuilderDateTimeSelector';
import { AssignmentBuilderQuizCard } from '@/components/features/assignments/builder/AssignmentBuilderQuizCard';
import AssignmentBuilderQuizDialog from '@/components/features/assignments/builder/AssignmentBuilderQuizDialog';
import { FilesMultipleSelector } from '@/components/features/files/file-multiple-selector';
import { GradeMappingPreview, GradeScaleField, GradeScaleSaveButton } from '@/components/features/grade-scale';
import { NavigationBlockerDialog } from '@/components/features/navigation/NavigationBlockerDialog';
import QuizBuilderMembersDialog from '@/components/features/quiz/quiz-builder/QuizBuilderMembersDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItemWithExpansion, RadioGroupItemWithLabel } from '@/components/ui/radio-group';
import { NumberSelect } from '@/components/ui/select';
import { Tag } from '@/components/ui/tags';
import { Tiptap, TiptapToolbar } from '@/components/ui/tiptap-editor';
import { useAssignmentBuilderForm } from '@/hooks/assignment';
import { postExtensionSet } from '@/lib/tiptap-extensions-sets';
import { getAvatar } from '@/utils/getAvatar';

export default function AssignmentBuilderPage() {
  const { isEdit, form, attachments, setAttachments, isDraftReady, clearDraft, isDirty, onSubmit, onInvalid, attachQuiz, detachQuiz, isPending, isLoadingRemote, submitError } =
    useAssignmentBuilderForm();

  const [membersDialogOpen, setMembersDialogOpen] = useState<boolean>(false);
  const [quizDialogOpen, setQuizDialogOpen] = useState<boolean>(false);

  const quiz = useWatch({ control: form.control, name: 'quiz' });
  const autoGrade = useWatch({ control: form.control, name: 'autoGrade' }) ?? false;
  const gradeMapping = useWatch({
    control: form.control,
    name: 'gradeMapping',
  });
  const users = useWatch({ control: form.control, name: 'users' }) ?? [];
  const groups = useWatch({ control: form.control, name: 'groups' }) ?? [];
  const nameValue = useWatch({ control: form.control, name: 'name' }) ?? '';
  const descValue = useWatch({ control: form.control, name: 'desc' }) ?? '';
  const maxAttemptsValue = useWatch({ control: form.control, name: 'maxAttempts' }) ?? 0;

  const setUsers = (next: UserPublic[]) => form.setValue('users', next, { shouldDirty: true, shouldValidate: true });
  const setGroups = (next: GroupPublic[]) => form.setValue('groups', next, { shouldDirty: true, shouldValidate: true });

  const removeUser = (id: number) => setUsers(users.filter((u) => u.id !== id));
  const removeGroup = (id: number) => setGroups(groups.filter((g) => g.id !== id));

  const maxAttemptsRadioValue = maxAttemptsValue === 0 ? '1' : '2';

  if (isLoadingRemote || (!isEdit && !isDraftReady)) {
    return (
      <div className="base-direction-col h-full overflow-hidden">
        <div className="bg-background viewport-height relative flex w-full items-center justify-center rounded-2xl border">
          <Loader2Icon className="size-5 animate-spin" aria-hidden />
        </div>
      </div>
    );
  }

  return (
    <>
      <NavigationBlockerDialog
        enabled={isDirty && !isPending}
        excludePaths={['/manage/assignments/:assignmentId']}
        onProceed={clearDraft}
        title="Czy na pewno chcesz wyjść?"
        description="Niezapisane zmiany zostaną utracone."
        confirmLabel="Wyjdź bez zapisywania"
      />
      <QuizBuilderMembersDialog open={membersDialogOpen} onOpenChange={setMembersDialogOpen} users={users} groups={groups} onUsersChange={setUsers} onGroupsChange={setGroups} />
      <AssignmentBuilderQuizDialog
        open={quizDialogOpen}
        onOpenChange={setQuizDialogOpen}
        selectedQuiz={quiz ?? null}
        onSelect={(next: QuizPublic | null) => {
          if (next) {
            attachQuiz(next);
          } else {
            detachQuiz();
          }
        }}
      />
      <div className="base-direction-col h-full overflow-hidden">
        <div className="bg-background viewport-height relative flex w-full flex-col overflow-hidden rounded-2xl border">
          <Form {...form}>
            <form noValidate id="assignment-builder-form" onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="relative flex grow flex-col overflow-auto">
              {isPending && (
                <div className="bg-background/50 absolute inset-0 z-20 m-auto flex flex-wrap items-center justify-center gap-3 backdrop-blur-[2px]">
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  <h5 className="font-medium">Zapisywanie...</h5>
                </div>
              )}

              <header className="border-border-primary flex flex-col gap-3 border-b p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex flex-col">
                    <h1 className="text-2xl font-semibold">{isEdit ? 'Edytuj zadanie' : 'Opcje Zadania'}</h1>
                    <p className="text-muted-foreground text-sm">Wypełnij opcje zadania</p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {quiz ? (
                      <Button type="button" variant={'outline'} onClick={detachQuiz}>
                        <MinusIcon />
                        Odłącz quiz
                      </Button>
                    ) : (
                      <Button type="button" variant={'outline'} onClick={() => setQuizDialogOpen(true)}>
                        <PlusIcon />
                        Załącz quiz
                      </Button>
                    )}
                    <GradeScaleSaveButton
                      formId="assignment-builder-form"
                      getGradeMapping={() => form.getValues('gradeMapping') ?? null}
                      setGradeMapping={(next) =>
                        form.setValue('gradeMapping', next, {
                          shouldDirty: true,
                        })
                      }
                      disabled={isPending}
                      showGradeOptions={!autoGrade}
                      submitOnSelect={false}
                    />
                  </div>
                </div>
                {submitError && (
                  <Alert variant="destructive" className="bg-destructive/5 border-border-destructive">
                    <AlertCircleIcon />
                    <AlertTitle>{submitError.title}</AlertTitle>
                    <AlertDescription>{submitError.detail}</AlertDescription>
                  </Alert>
                )}
              </header>

              {/* Body */}
              <div className="grid flex-1 grid-cols-1 gap-0 md:grid-cols-[minmax(0,1fr)_minmax(300px,340px)]">
                {/* Left column: title, description, files */}
                <div className="md:border-border-primary w-full space-y-8 p-4 md:border-r md:border-b-0">
                  <FormField
                    name="name"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Tytuł</FormLabel>
                        <FormControl>
                          <Input placeholder="Tytuł" maxLength={100} {...field} />
                        </FormControl>
                        <FormDescription className={cn('text-xs', nameValue.length > 100 && 'text-destructive')}>{nameValue.length}/100 znaków</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="desc"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Treść</FormLabel>
                        <div className="border-border-primary space-y-2 rounded-md border">
                          <FormControl>
                            <Tiptap
                              {...field}
                              placeholder="Co chcesz przekazać..."
                              extensions={postExtensionSet}
                              onUpdate={(res) => {
                                if (res.editor.isEmpty) {
                                  field.onChange('');
                                  return;
                                }
                                field.onChange(res.editor.getHTML());
                              }}
                              content={field.value ?? ''}
                              className="min-h-32 p-1"
                            >
                              <TiptapToolbar className="-top-4 rounded-t-sm p-3" />
                            </Tiptap>
                          </FormControl>
                        </div>
                        <FormDescription className={cn('text-xs', descValue.length > 2000 && 'text-destructive')}>{descValue.length}/2000 znaków</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground text-xs font-medium uppercase">Pliki ({attachments.length})</p>
                    </div>
                    <FilesMultipleSelector onFilesChange={setAttachments} initialFiles={attachments} buttonSize="sm" buttonVariant="outline" className="bg-transparent" />
                  </div>
                </div>

                {/* Right column: quiz preview, attempts, dates, groups */}
                <div className="space-y-8 p-4">
                  {quiz && (
                    <div className="space-y-3">
                      <h5 className="text-base font-semibold">Quiz</h5>
                      <AssignmentBuilderQuizCard quiz={quiz} />
                      <FormField
                        name="autoGrade"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center gap-2">
                            <FormControl>
                              <Checkbox
                                checked={field.value ?? false}
                                onCheckedChange={(checked) => {
                                  const next = checked === true;
                                  field.onChange(next);
                                  if (!next) {
                                    form.setValue('gradeMapping', null, {
                                      shouldDirty: true,
                                    });
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm">Automatyczne ocenianie z wyniku quizu</FormLabel>
                          </FormItem>
                        )}
                      />
                      {autoGrade && (
                        <div className="space-y-2">
                          <p className="text-text-tertiary text-sm font-medium">Skala ocen</p>
                          <p className="text-muted-foreground text-xs">Dziedziczona ze skali ocen quizu</p>
                          <GradeMappingPreview gradeMapping={gradeMapping} />
                        </div>
                      )}
                    </div>
                  )}

                  {!quiz && (
                    <div className="space-y-3">
                      <h5 className="text-base font-semibold">Ilość podejść</h5>
                      <FormField
                        name="maxAttempts"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <RadioGroup value={maxAttemptsRadioValue} onValueChange={(value) => field.onChange(value === '1' ? 0 : 1)} className="gap-4">
                                <RadioGroupItemWithLabel value="1" label="Nieograniczona ilość" description="Użytkownik ma nieograniczoną ilość podejść" />
                                <RadioGroupItemWithExpansion
                                  value="2"
                                  label="Ustaw ilość podejść"
                                  description="Ustaw ilość możliwych podejść dla użytkownika"
                                  selectedValue={maxAttemptsRadioValue}
                                  expansionContent={
                                    <NumberSelect
                                      value={String(field.value ?? 1)}
                                      onValueChange={(value) => field.onChange(Number(value))}
                                      disabled={maxAttemptsRadioValue !== '2'}
                                      start={1}
                                      end={10}
                                      size="sm"
                                      placeholder="Ilość podejść"
                                      className="w-30"
                                    />
                                  }
                                />
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {!autoGrade && (
                    <GradeScaleField
                      gradeMapping={gradeMapping}
                      onChange={(next) =>
                        form.setValue('gradeMapping', next, {
                          shouldDirty: true,
                        })
                      }
                    />
                  )}

                  <div className="space-y-3">
                    <h5 className="text-base font-semibold">Dostęp czasowy</h5>
                    <AssignmentBuilderDateTimeSelector />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h5 className="text-base font-semibold">Dodaj grupy</h5>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {groups.map(({ id, name, avatarURL }) => (
                        <Tag key={id} onRemove={() => removeGroup(id)} variant="muted" border>
                          <Avatar size="xs">
                            <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={name} />
                            <AvatarFallback>
                              <UsersIcon />
                            </AvatarFallback>
                          </Avatar>
                          {name}
                        </Tag>
                      ))}
                      {users.map(({ id, name, surname, avatarURL }) => (
                        <Tag key={id} onRemove={() => removeUser(id)} variant="muted" border>
                          <Avatar size="xs">
                            <AvatarImage src={avatarURL ? getAvatar(avatarURL) : undefined} alt={`${name}_${surname}`} />
                            <AvatarFallback>
                              <UserIcon />
                            </AvatarFallback>
                          </Avatar>
                          {name} {surname.at(0)}.
                        </Tag>
                      ))}
                      <Badge variant="muted" border onClick={() => setMembersDialogOpen(true)} className="cursor-pointer">
                        <PlusIcon />
                        Dodaj
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
}
